import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClaimLink, createDraft, listPartnerCommerces, type PartnerCommerce } from '@services/partnerService';
import { usePartnerAuth } from '../PartnerAuthContext';
import { Badge, Button, C, Card, ErrorNote, Field, Muted, PageShell, SectionTitle, Title } from '../ui';

const fmtDate = (s: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PartnerPortal = () => {
  const { token, partner, logout } = usePartnerAuth();
  const navigate = useNavigate();
  const [commerces, setCommerces] = useState<PartnerCommerce[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<Record<string, string>>({});

  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [ownerNombre, setOwnerNombre] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerWhatsapp, setOwnerWhatsapp] = useState('');

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      setCommerces(await listPartnerCommerces(token));
    } catch {
      setError('No se pudieron cargar los comercios.');
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setCreating(true);
    try {
      await createDraft(token, {
        negocio: { nombre: nombre.trim(), direccion: direccion.trim() || undefined },
        owner: { nombre: ownerNombre.trim(), email: ownerEmail.trim(), whatsapp: ownerWhatsapp.trim() || undefined },
        monthly_orders: 'menos_de_100',
        product_interest: ['menu'],
      });
      setNombre('');
      setDireccion('');
      setOwnerNombre('');
      setOwnerEmail('');
      setOwnerWhatsapp('');
      await refresh();
    } catch {
      setError('No se pudo crear el comercio. Revisa los datos.');
    } finally {
      setCreating(false);
    }
  };

  const onClaimLink = async (idComercio: string) => {
    if (!token) return;
    try {
      const res = await createClaimLink(token, idComercio);
      setLinks((p) => ({ ...p, [idComercio]: `${window.location.origin}/pre-registro/${res.token}` }));
    } catch {
      setError('No se pudo generar el link de claim.');
    }
  };

  const drafts = commerces.filter((c) => c.estado === 'draft').length;

  return (
    <PageShell>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <Title
          sub={`${partner?.nombre || partner?.usuario || ''} · ${commerces.length} comercio(s), ${drafts} sin reclamar`}
        >
          Portal de Partners
        </Title>
        <Button variant="secondary" onClick={logout} style={{ flexShrink: 0 }}>
          Salir
        </Button>
      </div>

      <Card>
        <SectionTitle>Pre-registrar un comercio</SectionTitle>
        <form onSubmit={onCreate}>
          <Field label="Nombre del negocio" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          <Field label="Direccion (opcional)" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
          <Field
            label="Nombre del dueno"
            value={ownerNombre}
            onChange={(e) => setOwnerNombre(e.target.value)}
            required
          />
          <Field
            label="Email del dueno"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            required
          />
          <Field
            label="WhatsApp del dueno"
            value={ownerWhatsapp}
            onChange={(e) => setOwnerWhatsapp(e.target.value)}
            placeholder="+52..."
          />
          {error && <ErrorNote>{error}</ErrorNote>}
          <Button type="submit" disabled={creating} style={{ marginTop: 16 }}>
            {creating ? 'Creando...' : 'Crear comercio'}
          </Button>
        </form>
      </Card>

      <Card>
        <SectionTitle>Mis comercios</SectionTitle>
        {loadingList && <Muted>Cargando...</Muted>}
        {!loadingList && commerces.length === 0 && <Muted>Aun no has registrado comercios.</Muted>}
        {commerces.map((c) => (
          <div key={c.id_suscripcion} style={{ borderTop: `1px solid ${C.cardBorder}`, padding: '14px 0' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{c.negocio_nombre || 'Comercio sin nombre'}</span>
                  <Badge tone={c.estado === 'reclamado' ? 'claimed' : 'draft'}>
                    {c.estado === 'reclamado' ? 'Reclamado' : 'Borrador'}
                  </Badge>
                </div>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
                  {c.owner_nombre || 'Sin dueno'}
                  {c.owner_telefono ? ` · ${c.owner_telefono}` : ''}
                  {c.owner_correo ? ` · ${c.owner_correo}` : ''}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {c.tipo_plan || 'FREEMIUM'}
                  {c.created_at ? ` · creado ${fmtDate(c.created_at)}` : ''}
                </div>
              </div>
              {c.estado === 'draft' && c.id_comercio && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <Button variant="ghost" onClick={() => navigate(`/partner/draft/${c.id_comercio}/menu`)}>
                    Cargar menu
                  </Button>
                  <Button variant="ghost" onClick={() => onClaimLink(c.id_comercio as string)}>
                    Link de claim
                  </Button>
                </div>
              )}
            </div>
            {c.id_comercio && links[c.id_comercio] && (
              <input
                readOnly
                value={links[c.id_comercio]}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  width: '100%',
                  marginTop: 10,
                  padding: '8px 10px',
                  fontSize: 12,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 6,
                  boxSizing: 'border-box',
                  fontFamily: 'Poppins, sans-serif',
                  color: C.secondaryDark,
                }}
              />
            )}
          </div>
        ))}
      </Card>
    </PageShell>
  );
};

export default PartnerPortal;
